/**
 * The account's sheets (part 36c; `cockatrice-ui`: `TwoFactor.jsx`'s sheet, and the screens the design lacks built in
 * its look, the user's choice 2026-10-24): each account action in a modal sheet over the page, its fresh proof first
 * (table PF: the password, then the second step when the account has one), then the action, then the relay's word on
 * it (CH5). The second step's setup (FX3, FX4) is here too, shared with the enrolment link's card. A grant goes from
 * its proof straight to its one action (C9); codes and a seed are shown once and kept nowhere (C15).
 */
import { createSignal, onCleanup, onMount } from 'solid-js';
import type { Accessor } from 'solid-js';
import { canonicalEmail } from '../account/email.ts';
import type { FactorKind, NamedFactor, NewFactorProof, Outcome } from '../account/actions.ts';
import type { RelayError } from '../account/client.ts';
import type { SecondProof } from '../account/flows.ts';
import { askPasskey, createPasskey } from '../account/passkey.boundary.ts';
import type { Advanced, ChangeEntry, DeviceEntry, FactorSetup, Granted, PasskeyRequest, SecondFactorNeeded } from '../account/wire.ts';
import { ErrorText, focusField, newStrength, problemWords } from './account-fields.ts';
import type { Strength } from '../account/strength.ts';
import { el } from '../ui/dom.ts';
import { accountLinkOf } from '../shell/route.ts';
import type { Child } from '../ui/dom.ts';
import { ModalBody, ModalButtons, ModalHead } from './modal.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import CodeInput from '../ui/CodeInput.ts';
import Field from '../ui/Field.ts';
import Icon from '../ui/Icon.ts';
import type { IconName } from '../ui/Icon.ts';
import PasswordField from '../ui/PasswordField.ts';
import type { PasswordStrength } from '../ui/PasswordField.ts';
import RadioGroup from '../ui/RadioGroup.ts';
import type { RadioOption } from '../ui/RadioGroup.ts';
import TextField from '../ui/TextField.ts';
import { component } from '../ui/solid.ts';
import { QrImage } from './qr.ts';
import type { Shell } from './shell.ts';
import type { AcceptOutcome } from '../shared/shares.ts';

/** What a sheet does (the dialog's task). */
export type AccountTask =
  | { readonly kind: 'password' }
  | { readonly kind: 'email' }
  | { readonly kind: 'revoke'; readonly device: DeviceEntry }
  | { readonly kind: 'cancel'; readonly change: ChangeEntry }
  | { readonly kind: 'factor' }
  | { readonly kind: 'remove-factor'; readonly factor: NamedFactor; readonly last: boolean }
  | { readonly kind: 'codes' }
  | { readonly kind: 'keep' }
  /** Part 38c, JL3: a waiting invite accepted (its claim and keyring entry need a fresh grant and the password). */
  | { readonly kind: 'accept' }
  /** Part 38c, AS8 (KG11): the guest keyring opened on this device with the password (made on another device since). */
  | { readonly kind: 'unlock' };

/** A date as the interface writes it (long, in the interface's language). */
export function dateWords(shell: Pick<Shell, 'locale'>, at: number): string {
  return new Intl.DateTimeFormat(shell.locale, { dateStyle: 'long' }).format(at);
}

/** A date and time as the interface writes it. */
export function timeWords(shell: Shell, at: number): string {
  return new Intl.DateTimeFormat(shell.locale, { dateStyle: 'long', timeStyle: 'short' }).format(at);
}

/** What a change is, in words (PD1). */
export function changeWords(shell: Shell, change: ChangeEntry): string {
  return shell.t(`account.change.${change.kind}`);
}

/**
 * A problem of an account action in words (PF3–PF10, CH6, FX9): the login's words, and the words an action meets that a
 * login can't (`grant-invalid`, a session ended, F turned off or on meanwhile, a thing gone).
 */
export function actionProblem(shell: Shell, problem: Exclude<Outcome<unknown>, { readonly kind: 'ok' }>): string {
  const key = problem.kind === 'refused' ? ACTION_WORDS[problem.error] : undefined;
  return key === undefined ? problemWords(shell, problem) : shell.t(key);
}

/** The words of an action's refusals that a login's don't say; every other refusal is said as the login's. */
const ACTION_WORDS: Partial<Readonly<Record<RelayError, 'account.error.wrongPassword' | 'account.error.grant' | 'account.error.session' | 'account.error.factorOff' | 'account.error.factorOn' | 'account.error.notFound' | 'account.error.linkHere'>>> = {
  'login-failed': 'account.error.wrongPassword',
  'grant-invalid': 'account.error.grant',
  'ticket-invalid': 'account.error.grant',
  'device-revoked': 'account.error.session',
  'device-expired': 'account.error.session',
  'login-required': 'account.error.session',
  'logged-out': 'account.error.session',
  unauthenticated: 'account.error.session',
  'account-deleted': 'account.error.session',
  'second-factor-off': 'account.error.factorOff',
  'second-factor-on': 'account.error.factorOn',
  'not-found': 'account.error.notFound',
  'invalid-link': 'account.error.linkHere',
};

/** The outcome of a change in words (CH5): `done` for what an applied change of this kind did. */
export function advancedWords(shell: Shell, advanced: Advanced, done: string, email: string): string {
  const { t } = shell;
  switch (advanced.kind) {
    case 'applied':
      return done;
    case 'check-email':
      return t('account.outcome.check', { email });
    case 'pending':
      return t('account.outcome.pending', { date: timeWords(shell, advanced.dueAt) });
    case 'void':
      return t('account.outcome.void');
  }
}

/** The sheet's header: the progress line, the heading (the dialog's name, focused as each step opens) and Close (part 41b: the backup password's sheets too). */
export function SheetHead(shell: Shell, progress: Accessor<string>, title: Accessor<string>): Child {
  // Part 11g: the modal's head (`modal.ts`), its step line above the name.
  return ModalHead(shell, title, { progress, closeClass: 'ac-sheet__close' });
}

/** Moves the focus to the sheet's heading (each step's first focus: its name read, then Tab reaches its first field). */
export function focusHeading(): void {
  queueMicrotask(() => document.getElementById('dialog-heading')?.focus());
}

/** A small icon of the library's, in a line of words. */
function SmallIcon(name: IconName): Child {
  return component(Icon, { name, size: 'sm' });
}

/** The sheets' illustration: a large icon in its circle (`ok`: the done mark's colour). */
export function Illustration(name: IconName, ok = false): Child {
  return el('div', { class: ok ? 'ac-illus ac-illus--ok' : 'ac-illus', 'aria-hidden': 'true' }, component(Icon, { name, size: 'lg' }));
}

/**
 * A field the sheets ask for (the library's Field), labelled `label`, `#id` on its control: required (as the old
 * input was) with no "Required" mark, since a sheet asks only for what it needs.
 */
function Asked(id: string, label: string, control: () => Child, help?: Child): Child {
  return component(Field, {
    id,
    label,
    help,
    required: true,
    hideMarker: true,
    get children() {
      return control();
    },
  });
}

/** A password field of the sheets' (the library's, its Show toggle in words of ours), its problems said in `describedBy`. */
export function SheetPassword(shell: Shell, id: string, autocomplete: string, set: (value: string) => void, label: string, describedBy = 'sheet-error'): Child {
  const { t } = shell;
  return Asked(id, label, () =>
    component(PasswordField, {
      autocomplete,
      'aria-describedby': describedBy,
      showLabel: t('account.show'),
      hideLabel: t('account.hide'),
      onChange: (value: string) => {
        set(value);
      },
    }),
  );
}

/** The library meter's score for a strength (empty shows no meter). */
const SCORES: Readonly<Record<Strength, PasswordStrength['score']>> = { empty: 0, short: 0, weak: 1, good: 3, strong: 4 };

/**
 * A new password's field (decision 25, SU3; part 41b: the backup password's): the library's, its meter scored and worded
 * by the account's strength rule, the rule itself as the help; `check` says what is wrong before a submit, or null.
 */
export function SheetNewPassword(shell: Shell, id: string, email: Accessor<string>, set: (password: string) => void, label: string, describedBy = 'sheet-error'): { readonly field: Child; readonly check: () => Promise<string | null> } {
  const { t } = shell;
  const typed = newStrength(shell, email);
  const field = Asked(
    id,
    label,
    () =>
      component(PasswordField, {
        autocomplete: 'new-password',
        'aria-describedby': describedBy,
        showLabel: t('account.show'),
        hideLabel: t('account.hide'),
        strength: (value: string): PasswordStrength => {
          const strength = typed.strength(value);
          return { score: SCORES[strength], label: t(`account.strength.${strength}`) };
        },
        // Decision 71: status announced once, without chatter: quiet while typing, said once on leaving the field.
        strengthLive: false,
        onChange: (value: string) => {
          typed.set(value);
          set(value);
        },
      }),
    t('account.strength.empty'),
  );
  return { field, check: typed.check };
}

/**
 * A sheet's submit (the library's Button) that says it is working (Argon2id takes about a second on a phone) and keeps
 * its place meanwhile; off (`aria-disabled`) while busy or `off`, yet pressable, so the form says what is missing.
 */
export function SheetSubmit(label: string, busy: Accessor<boolean>, working: string, off: () => boolean = () => false, variant: 'primary' | 'danger' = 'primary'): Child {
  return component(Button, {
    type: 'submit',
    variant,
    get 'aria-disabled'() {
      return busy() || off() ? 'true' : 'false';
    },
    get 'aria-busy'() {
      return busy() ? 'true' : 'false';
    },
    get children() {
      return busy() ? working : label;
    },
  });
}

/** A sheet's plain button (Cancel, Back, Not now: the library's ghost Button). */
export function SheetButton(label: string, onClick: () => void, className?: string, variant: 'ghost' | 'primary' = 'ghost'): Child {
  return component(Button, { variant, class: className, onClick, children: label });
}

/** The second step's proof (PF2, as the login's LG5): a passkey, a code from the app, or a backup code. */
function FactorStep(shell: Shell, need: SecondFactorNeeded, busy: Accessor<boolean>, prove: (proof: SecondProof) => void, setError: (text: string) => void): Child {
  const { t } = shell;
  const [code, setCode] = createSignal('');
  const [backup, setBackup] = createSignal(!need.totp);
  const request = need.passkey;
  const passkey = async (request: PasskeyRequest): Promise<void> => {
    const answer = await askPasskey(shell.credentials, request);
    if (answer === null) setError(t('account.error.passkey'));
    else prove({ passkey: answer });
  };
  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    const typed = code().trim();
    if (typed === '') {
      setError(t('account.error.noCode'));
      focusField('sheet-code');
      return;
    }
    prove(backup() ? { backupCode: typed } : { totp: typed.replace(/\s/gu, '') });
  };
  return el(
    'form',
    { class: 'c-modal__form proof-factor', noValidate: true, onSubmit: submit },
    ModalBody({},
      el('p', { class: 'ac-lead' }, t('account.proof.factorLead')),
      request === null
        ? null
        : component(Button, {
            size: 'lg',
            fullWidth: true,
            class: 'use-passkey',
            get 'aria-disabled'() {
              return busy() ? 'true' : 'false';
            },
            onClick: () => void passkey(request),
            children: t('account.second.passkey'),
          }),
      // A backup code has letters and dashes, so the code is a text field (what is typed is sent as typed, less spaces
      // for the app's: PF2), not the library's six-digit CodeInput.
      component(TextField, {
        id: 'sheet-code',
        label: () => (backup() ? t('account.second.backupLabel') : t('account.second.codeLabel')),
        autocomplete: 'one-time-code',
        inputMode: () => (backup() ? 'text' : 'numeric'),
        get value() {
          return code();
        },
        'aria-describedby': 'sheet-error',
        onChange: (value: string) => {
          setCode(value);
        },
      }),
      need.totp && need.backupCodes > 0
        ? el(
            'p',
            {},
            component(Button, {
              variant: 'link',
              class: 'switch-code',
              onClick: () => {
                setBackup(!backup());
                setCode('');
              },
              children: () => (backup() ? t('account.second.useApp') : t('account.second.useBackup')),
            }),
          )
        : null,
    ),
    ModalButtons(SheetSubmit(t('account.sheet.continue'), busy, t('account.working'))),
  );
}

/** FX4, C15: the backup codes shown once, with Download, Copy and Print, and "I have saved these codes". */
export function CodesView(shell: Shell, codes: readonly string[], email: string, saved: Accessor<boolean>, setSaved: (on: boolean) => void): Child {
  const { t } = shell;
  const [copied, setCopied] = createSignal('');
  const text = `${t('account.tf.codesFile', { email })}\n\n${codes.map((code, i) => `${String(i + 1).padStart(2, '0')}  ${code}`).join('\n')}\n`;
  const url = shell.fileUrl(new TextEncoder().encode(text), 'text/plain');
  onCleanup(() => {
    shell.revokeUrl(url);
  });
  return [
    el('p', { class: 'ac-lead' }, t('account.tf.codes')),
    el(
      'ol',
      { class: 'ac-codes print-only-keep', 'aria-label': t('account.tf.codesLabel') },
      ...codes.map((code, i) => el('li', {}, el('span', { class: 'ac-codes__n', 'aria-hidden': 'true' }, String(i + 1).padStart(2, '0')), el('code', { class: 'ac-mono' }, code))),
    ),
    el(
      'div',
      { class: 'ac-actions ac-actions--tools' },
      // A link to the file, as before: the browser saves it (the library's BackupCodes has a Download press, no file).
      component(Button, { size: 'sm', iconStart: 'download', class: 'codes-download', href: url, download: 'cockatrice-backup-codes.txt', children: t('account.tf.download') }),
      component(Button, {
        size: 'sm',
        iconStart: 'copy',
        class: 'codes-copy',
        onClick: () => {
          void shell.copy(codes.join('\n')).then((done) => {
            setCopied(done ? t('account.tf.copied') : '');
          });
        },
        children: t('account.tf.copy'),
      }),
      component(Button, {
        size: 'sm',
        iconStart: 'print',
        class: 'codes-print',
        onClick: () => {
          shell.print();
        },
        children: t('account.tf.print'),
      }),
      el('span', { class: 'ac-hint', role: 'status' }, copied),
    ),
    component(Checkbox, {
      id: 'codes-saved',
      label: t('account.tf.saved'),
      get checked() {
        return saved();
      },
      'aria-describedby': 'sheet-error',
      onChange: (checked: boolean) => {
        setSaved(checked);
      },
    }),
  ];
}

/**
 * Deep-fix r3, LK: a mailed link pasted in, for a device whose links open elsewhere (the iOS Home Screen app's open in
 * the browser, whose storage isn't the app's): a change's or an enrolment's link opens its card here, on the device that
 * asked; anything else is said not to be one.
 */
export function PasteLink(shell: Shell, setError: (text: string) => void, id = 'paste-link', errorId = 'sheet-error'): Child {
  const { t, ui } = shell;
  const [text, setText] = createSignal('');
  const open = (event: SubmitEvent): void => {
    event.preventDefault();
    const typed = text().trim();
    const link = accountLinkOf(typed.slice(Math.max(0, typed.indexOf('#'))));
    const kind = link?.kind;
    const token = link?.token ?? null;
    if (token === null || (kind !== 'confirm' && kind !== 'enrol')) {
      setError(t('account.paste.bad'));
      focusField(id);
      return;
    }
    ui.setDialog(null);
    ui.setLinkToken(token);
    ui.setAccountStep(kind);
  };
  // The field's help is `${id}-help` (the library Field's), read before the error.
  return el(
    'form',
    { class: 'ac-paste', noValidate: true, onSubmit: open },
    component(TextField, {
      id,
      type: 'url',
      label: t('account.paste.label'),
      help: t('account.paste.help'),
      autocomplete: 'off',
      'aria-describedby': errorId,
      get value() {
        return text();
      },
      onChange: (value: string) => {
        setText(value);
      },
    }),
    component(Button, { type: 'submit', size: 'sm', class: 'paste-open', children: t('account.paste.open') }),
  );
}

/** The base32 seed in groups of four, as the design shows the key to type. */
export function grouped(secret: string): string {
  return (secret.match(/.{1,4}/gu) ?? []).join(' ');
}

/**
 * FX3, FX4: the second step's setup and proof: the app's QR code, its key and the code field, or the passkey's prompt;
 * `confirm` takes the proof and answers what follows (the codes, or the change's outcome), shown by `after`.
 */
export function SetupStep(shell: Shell, setup: FactorSetup, busy: Accessor<boolean>, confirm: (proof: NewFactorProof) => void, setError: (text: string) => void): Child {
  const { t } = shell;
  const [code, setCode] = createSignal('');
  if (setup.kind === 'passkey') {
    // One prompt at a time: a second press while the browser's window is open asks nothing.
    let making = false;
    const make = async (): Promise<void> => {
      if (making) return;
      making = true;
      const made = await createPasskey(shell.credentials, setup.options);
      making = false;
      if (made === null) setError(t('account.error.passkeyMake'));
      else confirm({ passkey: made });
    };
    return el(
      'form',
      {
        class: 'c-modal__form setup-passkey',
        noValidate: true,
        onSubmit: (event: SubmitEvent) => {
          event.preventDefault();
          void make();
        },
      },
      ModalBody({},
        Illustration('fingerprint'),
        el('p', { class: 'ac-lead' }, t('account.tf.passkey')),
        el('p', { class: 'ac-note' }, SmallIcon('info'), el('span', {}, t('account.tf.passkeyNote'))),
      ),
      ModalButtons(SheetSubmit(t('account.tf.passkeySubmit'), busy, t('account.working'))),
    );
  }
  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    const typed = code().replace(/\D/gu, '');
    if (typed.length !== 6) {
      setError(t('account.error.noCode'));
      focusField('setup-code');
      return;
    }
    confirm({ totp: typed });
  };
  return el(
    'form',
    { class: 'c-modal__form setup-app', noValidate: true, onSubmit: submit },
    ModalBody({},
      el('p', { class: 'ac-lead' }, t('account.tf.app')),
      el(
        'div',
        { class: 'ac-qr-row' },
        QrImage(setup.uri, t('account.tf.qr')),
        el(
          'div',
          { class: 'ac-qr-alt' },
          el('div', { class: 'ac-hint', id: 'setup-key-label' }, t('account.tf.manual')),
          el('code', { class: 'ac-mono ac-manual-key', 'aria-labelledby': 'setup-key-label' }, grouped(setup.secret)),
          component(Button, { variant: 'link', class: 'setup-open', href: setup.uri, children: t('account.tf.openApp') }),
        ),
      ),
      // The library's one-time code, digits only and never cut: a code past six digits is refused by the submit ("Type
      // the code.", the focus back here: FX4), not sent as its first six.
      component(CodeInput, {
        id: 'setup-code',
        cut: false,
        label: t('account.tf.code'),
        'aria-describedby': 'sheet-error',
        onChange: (value: string) => {
          setCode(value);
        },
      }),
    ),
    ModalButtons(SheetSubmit(t('account.tf.appSubmit'), busy, t('account.working'), () => code().replace(/\D/gu, '').length !== 6)),
  );
}

/** The step a sheet is at. */
type Step =
  | { readonly kind: 'choose' }
  | { readonly kind: 'form' }
  | { readonly kind: 'factor'; readonly need: SecondFactorNeeded }
  | { readonly kind: 'setup'; readonly setup: FactorSetup; readonly change: number }
  | { readonly kind: 'codes'; readonly codes: readonly string[]; readonly outcome: Advanced }
  | { readonly kind: 'done'; readonly title: string; readonly words: string; readonly ok: boolean; readonly paste?: boolean };

/** Whether a passkey can be made here (`PublicKeyCredential` and the credentials container). */
function passkeysHere(shell: Shell): boolean {
  return shell.credentials !== undefined && typeof globalThis.PublicKeyCredential === 'function';
}

/** A sheet's title for its task at its step. */
function titleOf(shell: Shell, task: AccountTask, step: Step): string {
  const { t } = shell;
  if (step.kind === 'done') return step.title;
  if (step.kind === 'codes') return t('account.codes.title');
  if (step.kind === 'setup') return t(step.setup.kind === 'passkey' ? 'account.tf.title.passkey' : 'account.tf.title.app');
  switch (task.kind) {
    case 'password':
      return t('account.password.title');
    case 'email':
      return t('account.email.title');
    case 'revoke':
      return t('account.revoke.title');
    case 'cancel':
      return t(task.change.kind === 'reset' ? 'account.stopReset.title' : 'account.cancel.title');
    case 'factor':
      return t('account.tf.title.choose');
    case 'remove-factor':
      return t('account.removeFactor.title');
    case 'codes':
      return t('account.codes.title');
    case 'keep':
      return t('account.keepAccount.title');
    case 'accept':
      return t('join.accept.title');
    case 'unlock':
      return t('shared.unlock.title');
  }
}

/** Part 38c, CL2–CL8: what an Accept ended in, in words. */
export function acceptWords(shell: Shell, outcome: Exclude<AcceptOutcome, { readonly kind: 'accepted' }>): string {
  const { t } = shell;
  if (outcome.kind === 'keyring-refused') return t('join.error.keyring');
  if (outcome.kind === 'failed') return t('join.error.failed');
  if (outcome.kind === 'nothing') return t('join.accept.gone');
  const key = JOIN_WORDS[outcome.error];
  return key === undefined ? actionProblem(shell, { kind: 'refused', error: outcome.error, retryAfter: null }) : t(key);
}

/** CL2–CL4, CL8: the relay's refusals of an Accept said in the invite's words; any other as an action's. */
const JOIN_WORDS: Partial<Readonly<Record<RelayError, `join.error.${'invite-address' | 'invite-claimed' | 'invite-ended' | 'invite-gone' | 'change-pending'}`>>> = {
  'invite-address': 'join.error.invite-address',
  'invite-claimed': 'join.error.invite-claimed',
  'invite-ended': 'join.error.invite-ended',
  'invite-gone': 'join.error.invite-gone',
  'change-pending': 'join.error.change-pending',
};

/** The account action's sheet for `task`. */
export function AccountSheet(shell: Shell, task: AccountTask): Child {
  const { t, account, manager, ui } = shell;
  const email = (): string => {
    const status = account.status();
    return status.kind === 'account' ? status.email : '';
  };
  // FX1: whether the account has F, from its view (read again after every action).
  const factorOn = (): boolean => {
    const view = account.view();
    return view !== null && (view.secondFactor.totp || view.secondFactor.passkeys > 0);
  };
  const [step, setStep] = createSignal<Step>(task.kind === 'factor' ? { kind: 'choose' } : { kind: 'form' });
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [newPassword, setNewPassword] = createSignal('');
  const [newEmail, setNewEmail] = createSignal('');
  const [kind, setKind] = createSignal<FactorKind>(passkeysHere(shell) ? 'passkey' : 'totp');
  const [saved, setSaved] = createSignal(false);
  const strong = SheetNewPassword(shell, 'sheet-new-pw', email, setNewPassword, t('account.password.new'));
  const go = (next: Step): void => {
    setError('');
    setStep(next);
    focusHeading();
  };
  /** The sheet's last step: its words, and the design's done mark when the action is done (`ok`), not waiting or void. */
  const done = (title: string, words: string, ok = false): void => {
    go({ kind: 'done', title, words, ok });
  };
  /** CH5's outcome for a change, said with what its being applied did; CG3, CG4: whose address the link went to. */
  const advanced = (outcome: Advanced, applied: string): void => {
    if (outcome.kind === 'check-email') {
      // With F, a new address takes only its own link (CG4); without, the current address's comes first (CG3).
      const toNew = task.kind === 'email' && factorOn();
      go({ kind: 'done', title: t('account.outcome.checkTitle'), words: toNew ? t('account.outcome.checkNew', { email: newEmail() }) : t('account.outcome.check', { email: email() }), ok: false, paste: true });
      return;
    }
    done(titleOf(shell, task, step()), advancedWords(shell, outcome, applied, email()), outcome.kind === 'applied');
  };
  /** Runs `step`'s request with the sheet busy, and says its problem if it has one. */
  const run = async <T>(request: () => Promise<Outcome<T>>, then: (value: T) => void | Promise<void>): Promise<void> => {
    if (busy()) return;
    setBusy(true);
    setError('');
    const outcome = await request();
    setBusy(false);
    if (outcome.kind !== 'ok') {
      setError(actionProblem(shell, outcome));
      // PF6: a ticket that ended goes back to the password.
      if (outcome.kind === 'refused' && (outcome.error === 'ticket-invalid' || outcome.error === 'grant-invalid')) {
        // Deep-fix r4: back to the password, its reason kept (the step's change clears the error).
        go({ kind: 'form' });
        setError(actionProblem(shell, outcome));
      }
      return;
    }
    await then(outcome.value);
  };
  /** C9: what a grant is spent on, at once. */
  const act = async (grant: Granted): Promise<void> => {
    switch (task.kind) {
      case 'password':
        await run(() => manager.changePassword(grant.grant, newPassword()), (outcome) => {
          advanced(outcome, t('account.outcome.password'));
        });
        return;
      case 'email':
        await run(() => manager.changeEmail(grant.grant, newEmail()), (outcome) => {
          advanced(outcome, t('account.outcome.email'));
        });
        return;
      case 'revoke':
        await run(() => manager.revoke(grant.grant, task.device.id), (outcome) => {
          advanced(outcome, t('account.outcome.revoke'));
        });
        return;
      case 'cancel':
        await run(
          () => manager.cancel(grant.grant, task.change.id),
          () => {
            done(titleOf(shell, task, step()), t(task.change.kind === 'reset' ? 'account.stopReset.done' : 'account.cancel.done'), true);
          },
        );
        return;
      case 'factor':
        await run(() => manager.addFactor(grant.grant, kind()), (started) => {
          go({ kind: 'setup', setup: started.setup, change: started.change });
        });
        return;
      case 'remove-factor':
        await run(() => manager.removeFactor(grant.grant, task.factor), (outcome) => {
          advanced(outcome, t('account.outcome.factorRemoved'));
        });
        return;
      case 'codes':
        await run(() => manager.newCodes(grant.grant), (made) => {
          go({ kind: 'codes', codes: made.codes, outcome: made.outcome });
        });
        return;
      case 'keep':
        await run(
          () => manager.cancelDeletion(grant.grant),
          () => {
            done(titleOf(shell, task, step()), t('account.keepAccount.done'), true);
          },
        );
        return;
      case 'accept': {
        // CL: the claim and the keyring's entry under this grant; a refusal said on this step, the invite kept or not
        // as the table says (CL6: a grant spent meanwhile goes back to the password).
        setBusy(true);
        const outcome = await manager.acceptInvite(grant.grant);
        setBusy(false);
        if (outcome.kind === 'accepted') {
          done(titleOf(shell, task, step()), t('join.accept.done'), true);
          return;
        }
        // PW2: claimed in another tab meanwhile (or put away there): nothing left to accept here.
        if (outcome.kind === 'nothing') {
          done(titleOf(shell, task, step()), t('join.accept.gone'), false);
          return;
        }
        if (outcome.kind === 'refused' && outcome.error === 'grant-invalid') go({ kind: 'form' });
        setError(acceptWords(shell, outcome));
        return;
      }
      case 'unlock': {
        // AS8: the proof's password opens the keyring; refused, it is said as an Accept's (KG4).
        setBusy(true);
        const opened = await manager.openKeyring();
        setBusy(false);
        if (opened === 'opened' || opened === 'none') {
          done(titleOf(shell, task, step()), t(opened === 'opened' ? 'shared.unlock.done' : 'shared.unlock.none'), opened === 'opened');
          return;
        }
        setError(t(opened === 'refused' ? 'join.error.keyring' : 'join.error.failed'));
        return;
      }
    }
  };
  /** PF1: the form's fields checked, then the proof (or, for F off, the enrolment's start: EN1). */
  const submitForm = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    if (password() === '') {
      setError(t('account.error.password'));
      focusField('sheet-pw');
      return;
    }
    // Busy while the strength check waits for its scorer, so a second press meanwhile sends nothing.
    setBusy(true);
    const weak = task.kind === 'password' ? await strong.check() : null;
    setBusy(false);
    if (weak !== null) {
      setError(weak);
      focusField('sheet-new-pw');
      return;
    }
    if (task.kind === 'email') {
      const next = canonicalEmail(newEmail());
      if (next === null || next === email()) {
        setError(next === null ? t('account.error.email') : t('account.error.sameEmail'));
        focusField('sheet-email');
        return;
      }
    }
    if (task.kind === 'factor' && !factorOn()) {
      await run(() => manager.startEnrolment(password(), kind()), () => {
        go({ kind: 'done', title: t('account.outcome.checkTitle'), words: t('account.tf.checkEmail', { email: email() }), ok: false, paste: true });
      });
      return;
    }
    await run(() => manager.prove(password()), async (answer) => {
      if (answer.kind === 'second-factor') go({ kind: 'factor', need: answer });
      else await act(answer);
    });
  };
  const proveSecond = (need: SecondFactorNeeded) => (proof: SecondProof): void => {
    void run(() => manager.proveSecond(need.ticket, proof), (grant) => act(grant));
  };
  const confirmSetup = (change: number) => (proof: NewFactorProof): void => {
    void run(() => manager.confirmAdd(change, proof), (outcome) => {
      advanced(outcome, t('account.outcome.factorAdded'));
    });
  };
  const progress = (): string => {
    const now = step();
    if (task.kind !== 'factor') return t('account.sheet.security');
    const order = ['choose', 'form', 'setup'];
    const at = order.indexOf(now.kind === 'factor' ? 'form' : now.kind);
    return at < 0 ? t('account.sheet.security') : t('account.sheet.step', { step: at + 1, total: 3 });
  };
  const lead = (): string => {
    switch (task.kind) {
      case 'revoke':
        return t('account.revoke.lead', { date: dateWords(shell, task.device.ageFrom) });
      case 'cancel':
        return task.change.kind === 'reset' ? t('account.stopReset.lead') : t('account.cancel.lead', { change: changeWords(shell, task.change) });
      case 'remove-factor':
        return task.last ? t('account.removeFactor.last') : t('account.removeFactor.lead');
      case 'codes':
        return t('account.codes.lead');
      case 'keep':
        return t('account.keepAccount.lead');
      case 'accept':
        return t('join.accept.lead');
      case 'unlock':
        return t('shared.unlock.lead');
      case 'password':
      case 'email':
      case 'factor':
        return t('account.proof.lead');
    }
  };
  const submitWords = (): string => {
    switch (task.kind) {
      case 'password':
        return t('account.password.submit');
      case 'email':
        return t('account.email.submit');
      case 'revoke':
        return t('account.revoke.submit');
      case 'cancel':
        return t(task.change.kind === 'reset' ? 'account.stopReset.submit' : 'account.cancel.submit');
      case 'remove-factor':
        return t('account.removeFactor.submit');
      case 'codes':
        return t('account.codes.submit');
      case 'keep':
        return t('account.keepAccount.submit');
      case 'accept':
        return t('join.accept.button');
      case 'unlock':
        return t('shared.unlock.submit');
      case 'factor':
        return t('account.sheet.continue');
    }
  };
  const Form = (): Child =>
    el(
      'form',
      { class: 'c-modal__form proof', noValidate: true, onSubmit: (event: SubmitEvent) => void submitForm(event) },
      ModalBody({},
        el('p', { class: 'ac-lead' }, lead()),
        SheetPassword(shell, 'sheet-pw', 'current-password', setPassword, task.kind === 'password' ? t('account.password.current') : t('account.password')),
        task.kind === 'password' ? strong.field : null,
        task.kind === 'email'
          ? Asked('sheet-email', t('account.email.new'), () =>
              component(TextField, {
                type: 'email',
                autocomplete: 'email',
                inputMode: 'email',
                'aria-describedby': 'sheet-error',
                get value() {
                  return newEmail();
                },
                onChange: (value: string) => {
                  setNewEmail(value);
                },
              }),
            )
          : null,
        el('p', { class: 'ac-hint' }, t('account.proof.forgot')),
      ),
      ModalButtons(
        task.kind === 'factor'
          ? SheetButton(t('account.back'), () => {
              go({ kind: 'choose' });
            })
          : SheetButton(t('account.sheet.cancel'), () => ui.setDialog(null), 'sheet-cancel'),
        SheetSubmit(submitWords(), busy, t('account.working'), () => false, task.kind === 'remove-factor' || task.kind === 'revoke' ? 'danger' : 'primary'),
      ),
    );
  const Choose = (): Child =>
    el(
      'div',
      { class: 'c-modal__form choose' },
      ModalBody({},
        el('p', { class: 'ac-lead' }, t('account.tf.choose')),
        // The design's radio cards (the library's RadioGroup): native radios, so the arrows move the choice and the
        // focus with it, and a passkey is offered only where one can be made.
        component(RadioGroup, {
          variant: 'cards',
          label: t('account.tf.choiceLabel'),
          hideLabel: true,
          options: [
            ...(passkeysHere(shell) ? [{ value: 'passkey', icon: 'fingerprint', label: t('account.tf.passkeyTitle'), badge: t('account.tf.recommended'), description: t('account.tf.passkeyText') } satisfies RadioOption] : []),
            { value: 'totp', icon: 'phone', label: t('account.tf.appTitle'), description: t('account.tf.appText') } satisfies RadioOption,
          ],
          get value() {
            return kind();
          },
          onChange: (value: string) => {
            setKind(value === 'passkey' ? 'passkey' : 'totp');
          },
        }),
      ),
      ModalButtons(
        SheetButton(t('account.sheet.notNow'), () => ui.setDialog(null)),
        SheetButton(
          t('account.sheet.continue'),
          () => {
            go({ kind: 'form' });
          },
          undefined,
          'primary',
        ),
      ),
    );
  const Codes = (codes: readonly string[], outcome: Advanced): Child =>
    el(
      'form',
      {
        class: 'c-modal__form codes',
        noValidate: true,
        onSubmit: (event: SubmitEvent) => {
          event.preventDefault();
          if (!saved()) {
            setError(t('account.error.saved'));
            focusField('codes-saved');
            return;
          }
          advanced(outcome, t('account.outcome.codes'));
        },
      },
      ModalBody({}, CodesView(shell, codes, email(), saved, setSaved), outcome.kind === 'pending' ? el('p', { class: 'ac-note' }, SmallIcon('info'), el('span', {}, t('account.codes.from', { date: timeWords(shell, outcome.dueAt) }))) : null),
      ModalButtons(SheetSubmit(t('account.sheet.continue'), busy, t('account.working'), () => !saved())),
    );
  const Done = (words: string, ok: boolean, paste: boolean): Child =>
    el(
      'div',
      { class: 'c-modal__form outcome' },
      ModalBody({},
        ok ? Illustration('check', true) : null,
        el('p', { class: 'ac-lead', role: 'status' }, words),
        paste ? PasteLink(shell, setError) : null,
      ),
      ModalButtons(SheetButton(t('account.sheet.done'), () => ui.setDialog(null), 'sheet-done', 'primary')),
    );
  onMount(focusHeading);
  return [
    SheetHead(shell, progress, () => titleOf(shell, task, step())),
    () => {
      const now = step();
      switch (now.kind) {
        case 'choose':
          return Choose();
        case 'form':
          return Form();
        case 'factor':
          return FactorStep(shell, now.need, busy, proveSecond(now.need), setError);
        case 'setup':
          return SetupStep(shell, now.setup, busy, confirmSetup(now.change), setError);
        case 'codes':
          return Codes(now.codes, now.outcome);
        case 'done':
          return Done(now.words, now.ok, now.paste === true);
      }
    },
    el('div', { class: 'c-modal__error' }, ErrorText('sheet-error', error)),
  ];
}
